import type { PaletteMode, PaletteOptions } from '@mui/material/styles';
import type { ThemePreset } from '@/types';

interface PresetScale {
  main: string;
  light: string;
  dark: string;
}

interface ThemePresetConfig {
  label: string;
  primary: PresetScale;
  secondary: PresetScale;
  accent: PresetScale;
}

export const defaultThemePreset: ThemePreset = 'sunset';

export const colorPresets: Record<ThemePreset, ThemePresetConfig> = {
  sunset: {
    label: 'Sunset Bloom',
    primary:   { main: '#FF6B6B', light: '#FF8A8A', dark: '#E05555' },
    secondary: { main: '#4ECDC4', light: '#7BE0D9', dark: '#31A79F' },
    accent:    { main: '#FFD93D', light: '#FFE57A', dark: '#EAB308' },
  },
  ocean: {
    label: 'Ocean Calm',
    primary:   { main: '#5B8DEF', light: '#8FB2FF', dark: '#356CD5' },
    secondary: { main: '#14B8A6', light: '#5EEAD4', dark: '#0F8F82' },
    accent:    { main: '#F59E0B', light: '#FCD34D', dark: '#D97706' },
  },
  forest: {
    label: 'Forest Glow',
    primary:   { main: '#22C55E', light: '#4ADE80', dark: '#16A34A' },
    secondary: { main: '#0EA5E9', light: '#38BDF8', dark: '#0284C7' },
    accent:    { main: '#FACC15', light: '#FDE047', dark: '#CA8A04' },
  },
  rose: {
    label: 'Rose Petal',
    primary:   { main: '#F43F5E', light: '#FB7185', dark: '#BE123C' },
    secondary: { main: '#A855F7', light: '#C084FC', dark: '#7E22CE' },
    accent:    { main: '#F97316', light: '#FB923C', dark: '#C2410C' },
  },
  midnight: {
    label: 'Midnight Blue',
    primary:   { main: '#6366F1', light: '#818CF8', dark: '#4338CA' },
    secondary: { main: '#06B6D4', light: '#22D3EE', dark: '#0E7490' },
    accent:    { main: '#10B981', light: '#34D399', dark: '#047857' },
  },
  aurora: {
    label: 'Aurora Borealis',
    primary:   { main: '#8B5CF6', light: '#A78BFA', dark: '#6D28D9' },
    secondary: { main: '#34D399', light: '#6EE7B7', dark: '#059669' },
    accent:    { main: '#F59E0B', light: '#FCD34D', dark: '#B45309' },
  },
  candy: {
    label: 'Candy Pop',
    primary:   { main: '#EC4899', light: '#F472B6', dark: '#BE185D' },
    secondary: { main: '#FBBF24', light: '#FCD34D', dark: '#D97706' },
    accent:    { main: '#06B6D4', light: '#22D3EE', dark: '#0E7490' },
  },
  ember: {
    label: 'Ember Charcoal',
    primary:   { main: '#F26522', light: '#F5844F', dark: '#D44F12' },
    secondary: { main: '#212529', light: '#343A40', dark: '#121416' },
    accent:    { main: '#FFB703', light: '#FFC933', dark: '#E09F00' },
  },
  custom: {
    // Static fallback — ThemeContext overrides primary at runtime with user-selected colours
    label: 'Custom',
    primary:   { main: '#FF6B6B', light: '#FF8A8A', dark: '#E05555' },
    secondary: { main: '#4ECDC4', light: '#7BE0D9', dark: '#31A79F' },
    accent:    { main: '#FFD93D', light: '#FFE57A', dark: '#EAB308' },
  },
};

export function buildPalette(
  mode: PaletteMode,
  presetName: ThemePreset,
  /** Overrides primary + secondary with the given HSL values (used by rainbow preset) */
  dynamicColors?: { primary: string; secondary: string; accent: string },
): PaletteOptions {
  const preset = colorPresets[presetName];
  const primary   = dynamicColors ? { main: dynamicColors.primary,   light: dynamicColors.primary,   dark: dynamicColors.primary   } : preset.primary;
  const secondary = dynamicColors ? { main: dynamicColors.secondary, light: dynamicColors.secondary, dark: dynamicColors.secondary } : preset.secondary;
  const accent    = dynamicColors ? { main: dynamicColors.accent,    light: dynamicColors.accent,    dark: dynamicColors.accent    } : preset.accent;

  const isDark = mode === 'dark';

  return {
    mode,
    primary: {
      ...primary,
      contrastText: isDark ? '#08111F' : '#FFFFFF',
    },
    secondary: {
      ...secondary,
      contrastText: isDark ? '#08111F' : '#FFFFFF',
    },
    accent: {
      ...accent,
      contrastText: '#1F2937',
    },
    success: { main: '#22C55E', light: '#86EFAC', dark: '#15803D', contrastText: '#FFFFFF' },
    warning: { main: '#F59E0B', light: '#FCD34D', dark: '#B45309', contrastText: '#FFFFFF' },
    error: { main: '#EF4444', light: '#FCA5A5', dark: '#B91C1C', contrastText: '#FFFFFF' },
    info: { main: '#3B82F6', light: '#93C5FD', dark: '#1D4ED8', contrastText: '#FFFFFF' },
    background: {
      default: isDark ? '#07111F' : '#F4F7FB',
      paper: isDark ? 'rgba(15, 23, 42, 0.8)' : 'rgba(255, 255, 255, 0.82)',
    },
    surface: {
      sunken: isDark ? '#0B1526' : '#EAF0F7',
      elevated: isDark ? '#111C2F' : '#FFFFFF',
      glass: isDark ? 'rgba(15, 23, 42, 0.62)' : 'rgba(255, 255, 255, 0.58)',
    },
    text: {
      primary: isDark ? '#F8FAFC' : '#1F2937',
      secondary: isDark ? '#94A3B8' : '#6B7280',
      disabled: isDark ? '#64748B' : '#94A3B8',
    },
    divider: isDark ? 'rgba(148, 163, 184, 0.16)' : 'rgba(148, 163, 184, 0.2)',
    action: {
      hover: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.04)',
      selected: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)',
      focus: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(15,23,42,0.12)',
    },
  };
}